import React from 'react';
import Joi from 'joi';
import PropTypes  from 'prop-types';
import _ from 'lodash';

import { getCurrentUser } from '../../services/authService';
import { Facility, Organization, User } from '../../utils/models';
import { USER_TYPES } from '../../utils/constants';
import { getOrganization } from '../../services/organizationService';

import Conditional from '../common/conditional';
import Form from '../common/form';
import Progress from '../themed/progress';
import Skeleton from '../common/skeleton';

import withOrganizations from '../hoc/withOrganizations';
import withInspectionTypes from '../hoc/withInspectionTypes';


interface State {
	data: {
		organisationId: string
		facilityId: string
		module: string
		inspectionTypeId: string
		year: number
	}
	errors: Record<string,string>
	loading: boolean
	progress: number
	organization: Organization | null;
}

class GeneralReportOverviewForm extends Form {
	state: State = {
		data: { 
			organisationId: '', 
			facilityId: '', 
			module: '', 
			inspectionTypeId: '', 
			year: new Date().getFullYear()
		},
		errors: {},
		loading: false,
		progress: 0,
		organization: null,
	};

	schema = {
		organisationId: Joi.string().required().label('Organization'),
		facilityId: Joi.string().required().label('Facility'),
		module: Joi.string().required().label('Module'),
		inspectionTypeId: Joi.string().required().label('Inspection Type'),
		year: Joi.number().max(new Date().getFullYear()).optional().label('Year')
	};

	static propTypes = {
		onTrack: PropTypes.func
	}

	user = getCurrentUser() as User;

	componentDidMount() {
		if (this.user.userType === USER_TYPES.EXTERNAL) {
			this.handleSelectUserOrganization();
		}
	}

	componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<State>, snapshot?: any): void {
		const prevOrganisation = prevState.data.organisationId;
		const currentOrganisation = this.state.data.organisationId;

		if (prevOrganisation !== currentOrganisation && currentOrganisation) {
			this.getOrganization();
		}
	}

	handleSelectUserOrganization = () => {
		this.setState({ data: { ...this.state.data, organisationId: this.user.organisationId }});
	}

	getOrganization = async () => {
		try {
			this.setState({ loading: true });

			const response = await getOrganization(this.state.data.organisationId);
			this.setState({ organization: response.data, loading: false } as any);
		} catch (error) {
			this.setState({ loading: false });
		} 
	};

	getFacilitiesByOrganization = () => {
		if (this.state.organization) {
			return (this.state.organization as Organization).facilities.filter((facility) => facility.isActive).map((facility) => ({ id: facility._id, name: facility.facilityName, modules: facility.modules }));
		}

		return [];
	};
	
	getModulesByFacility = () => {
		const facilities = this.getFacilitiesByOrganization();
		const facility = facilities.find((facility) => facility.id === this.state.data.facilityId);
		 
		if (facility) {
		return facility.modules.map((module) => ({ id: module.name, name: module.name, inspectableItems: module.inspectableItems }));
		}

		return [];
	};

	getInspectableItemsFromModule = () => {
		const modules: Facility['modules'] = this.getModulesByFacility();

		if (modules.length > 0) {
			const moduleMatch = modules.find((module) => module.name === this.state.data.module);
			return moduleMatch ? moduleMatch.inspectableItems : [];
		}

		return [];
	};

	doSubmit = () => {
		const data = {
			organisationId: this.state.data.organisationId,
			facilityId: this.state.data.facilityId,
			module: this.state.data.module,
			inspectionTypeId: this.state.data.inspectionTypeId,
			year: this.state.data.year,
			inspectableItems: this.getInspectableItemsFromModule(),
		};

		this.props.onTrack(data);
	};

	render() {
		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<form autoComplete="off" onSubmit={this.handleSubmit}>
					<h3 className="form-title">View General Report Inspection Data</h3>
					<p className="form-subtitle">Overview reports are only available for ExxonMobil Piping, Equipment and Structure Reports</p>

					<div className="container">
						<Conditional isVisible={!this.state.loading}>
							<div className="row">
								<div className="col">
									{this.renderInput({
										type: 'number',
										name: 'year',
										label: 'Year',
										placeholder: 'Enter Project Creation Year',
									})}
								</div>

								<Conditional isVisible={this.user.userType === USER_TYPES.INTERNAL}>
									<div className="col">
										{this.renderSelect({
											name: 'organisationId',
											label: 'Organization',
											placeholder: 'Select an Organization',
											options: this.props.organizationsForDropdown,
										})}
									</div>
									
								</Conditional>
								
								<div className="col">
									{this.renderSelect({
										name: 'facilityId',
										label: 'Facility',
										placeholder: 'Select a Facility',
										options: this.getFacilitiesByOrganization()
									})}
								</div>
								
								<Conditional isVisible={this.state.data.facilityId.length > 0}>
									<div className="col">
										{this.renderSelect({
											name: 'module',
											label: 'Module',
											placeholder: 'Select a Module',
											options: this.getModulesByFacility()
										})}
									</div>
								</Conditional>
								
								<Conditional isVisible={this.state.data.module.length > 0}>
									<div className="col">
										{this.renderSelect({
											name: 'inspectionTypeId',
											label: 'Inspection Type',
											placeholder: 'Select an Inspection Type',
											options: this.props.types,
										})}
									</div>
								</Conditional>
							</div>
						</Conditional>

						<Conditional isVisible={this.state.loading}>
							<div className="row">
								{_.range(1, 6).map((fill) => (
									<div className='col' key={fill}>
										<Skeleton height="2.5rem" />
									</div>
								))}
							</div>
						</Conditional>
						
						<div className="d-flex justify-content-end">
							{this.renderButton('Track Progress')}
						</div>
					</div>
				</form>
			</React.Fragment>
		);
	}
}

export default withInspectionTypes(withOrganizations(GeneralReportOverviewForm));
